/*
 * ONE READING OF A RUN, for every view that draws one.
 *
 * Two surfaces render the same agent runs — the Trace rows and the Pipes canvas — and both need the
 * same four judgements: what state this run is in, what to call it, how long it has taken, and what
 * it has spent. Those were written inside the rows view, which is fine until the second reader
 * appears and gets its own copy; that is how one view starts calling a budget-capped run "done"
 * while the other calls it "stopped", and neither is wrong about anything except the other.
 *
 * Pure and DOM-free: rows in, strings out.
 */
import { t } from '../i18n';
/** RunStatus, as the generated enum's numeric values. Named so the callers read. */
export const QUEUED = 1;
export const RUNNING = 2;
export const AWAITING = 3;
export const FINISHED = 4;
/** TerminalReason.COMPLETED — the only self-declared success. Every other terminal is a stop. */
export const COMPLETED = 1;
/**
 * What state to paint this run in.
 *
 * A finished run is NOT automatically a successful one: COMPLETED is the only self-declared success,
 * and every other terminal reason — step cap, budget, no credits, aborted, error — is a run that
 * stopped. Painting them all green reports failures as done, which is the one reading of a run that
 * must never be wrong.
 */
export function runState(run) {
    if (run.status !== FINISHED) {
        return run.status === RUNNING ? 'running' : run.status === AWAITING ? 'awaiting' : 'queued';
    }
    return run.terminalReason === COMPLETED ? 'done' : 'stopped';
}
/**
 * What to CALL that state, in the reader's language.
 *
 * Beside `runState` because they are one reading: a surface that computes the state and then spells
 * it itself is how two views end up disagreeing about the same run in words after agreeing in
 * colour. A switch over literal keys, never `t(`ai.run_${state}`)` — a built key defeats both the
 * type checker and the extractor, so a missing translation would only show up in front of someone.
 */
export function runStateLabel(state) {
    switch (state) {
        case 'running':
            return t('ai.runRunning');
        case 'awaiting':
            return t('ai.runAwaiting');
        case 'queued':
            return t('ai.runQueued');
        case 'done':
            return t('ai.runCompleted');
        case 'stopped':
            return t('ai.runStopped');
    }
}
/** The name to show. The task's own label first — it is what the parent called this job. */
export function runLabel(run) {
    return run.task?.label || run.expertId || t('ai.runUntitled');
}
/** Wall-clock for a run — live while it is going, frozen once it ends. */
export function runElapsed(run) {
    const start = Number(run.startedAtNs / 1000000n);
    if (!start)
        return '';
    const end = run.endedAtNs ? Number(run.endedAtNs / 1000000n) : Date.now();
    const s = Math.max(0, (end - start) / 1000);
    return s < 60 ? `${s.toFixed(s < 10 ? 1 : 0)}s` : `${Math.floor(s / 60)}m${String(Math.round(s % 60)).padStart(2, '0')}s`;
}
/** What this run and everything under it has spent. Absent stays absent — a run whose provider
 *  reported no cost shows no figure rather than a confident zero. */
/*
 * ITS OWN SPEND PLUS ITS CHILDREN'S — and the first half was missing.
 *
 * `subtree_usage` means, precisely, the total of everything BENEATH a run, NOT counting the run
 * itself (runs.ts, `withSubtreeUsage`). That is a defensible thing for the field to mean, and it is
 * the wrong thing to read on its own: a leaf agent — one that spawned nothing, which is most of
 * them — has an empty subtree, so every leaf reported zero cost and zero tokens while its own
 * `usage` row sat there populated. Seen live: a completed agent whose card carried no figures at
 * all, and a header that inherited the same blank.
 *
 * "What did this agent cost" means this agent AND everything it started. Summed here, in the one
 * reading every view shares, rather than by changing what the stored field means underneath it.
 */
export function runCost(run) {
    const usd = (run.usage?.costUsd ?? 0) + (run.subtreeUsage?.costUsd ?? 0);
    if (!usd)
        return '';
    return usd >= 0.01 ? ` · $${usd.toFixed(3)}` : ` · $${usd.toFixed(4)}`;
}
/** Tokens for this run and its subtree, compactly. Absent stays absent, for the same reason cost does. */
/**
 * WHAT A WHOLE FOREST HAS SPENT — the header's two numbers.
 *
 * Summed over the ROOTS only. Every root already carries its own subtree's usage (`listTree`
 * derives it on read), so walking the children would count every descendant twice — the mistake
 * that makes a total look plausible and be wrong by a factor of the tree's depth.
 *
 * Live by construction: subtree usage is derived from the rows on every poll rather than rolled up
 * when a child finishes, so a running fan-out's total climbs while it is climbing.
 */
export function forestUsage(roots) {
    let tokens = 0;
    let costUsd = 0;
    for (const { run } of roots) {
        tokens +=
            Number(run.usage?.inputTokens ?? 0n) +
                Number(run.usage?.outputTokens ?? 0n) +
                Number(run.subtreeUsage?.inputTokens ?? 0n) +
                Number(run.subtreeUsage?.outputTokens ?? 0n);
        costUsd += (run.usage?.costUsd ?? 0) + (run.subtreeUsage?.costUsd ?? 0);
    }
    return { tokens, costUsd };
}
/**
 * A COST, in the one spelling every surface uses.
 *
 * Four decimals under a cent, because a single cheap turn costs a fraction of one and a figure
 * rounded to $0.00 would sit still while the number behind it climbed — worse than showing nothing,
 * since it looks like a working readout reporting zero.
 *
 * Here rather than in the chat header, which is where it was: three surfaces now show a cost (the
 * thread's, a card's, the canvas header's total) and two spellings of "what money looks like" is
 * how one of them starts rounding differently from the others.
 */
export function fmtUsd(usd) {
    if (usd >= 1)
        return `$${usd.toFixed(2)}`;
    if (usd >= 0.01)
        return `$${usd.toFixed(3)}`;
    return `$${usd.toFixed(4)}`;
}
/** A token count, in the header's and the card's shared shorthand. */
export const fmtTokenCount = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
export function runTokens(run) {
    const n = Number(run.usage?.inputTokens ?? 0n) +
        Number(run.usage?.outputTokens ?? 0n) +
        Number(run.subtreeUsage?.inputTokens ?? 0n) +
        Number(run.subtreeUsage?.outputTokens ?? 0n);
    if (!n)
        return '';
    return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}
//# sourceMappingURL=run-format.js.map